import { ariaAttributeProps, dataAttributeProps } from '@pbk-web/utils';
import type { MouseEvent, ReactElement } from 'react';
import { useMemo } from 'react';

import useSlot from '../../hooks/useSlots';
import type { SlotDefinitions } from '../../wrappers/withSlot';
import withSlots from '../../wrappers/withSlot';
import Loader from '../Loader';
import Tooltip from '../tooltip/Tooltip';

export const iconButtonContainerClasses =
  'flex items-center justify-center rounded-md bg-greyscale-5 text-text-2 max-h-[30px] h-[30px] min-w-[30px] px-1 cursor-pointer hover:bg-greyscale-5';
export const iconButtonClasses = `${iconButtonContainerClasses} w-9 h-8 py-1 `;
export const iconButtonDisabledClasses = `pointer-events-none opacity-50`;

type ButtonProps = {
  id?: string;
  type?: ButtonType;
  name?: string;
  htmlType?: 'button' | 'submit' | 'reset' | undefined;
  disabled?: boolean;
  className?: string; // This prop maps to additional classes that can be added from where the component is being used
  borderClass?: string;
  size?: ButtonSize;
  ignoreMinWidth?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  title?: string | ReactElement;
  toggleEnabled?: boolean;
  loading?: boolean;
  containerClassName?: string;
};

export enum ButtonType {
  PRIMARY,
  SECONDARY,
  TOGGLE,
  TERTIARY,
  ICON,
}

export enum ButtonSize {
  S,
  M,
}

const Button = withSlots<ButtonProps, SlotDefinitions<['Icon', 'SuffixIcon']>>((props) => {
  const {
    className,
    disabled,
    onClick,
    htmlType = 'button',
    children,
    name,
    size,
    type,
    borderClass = 'rounded-lg',
    ignoreMinWidth,
    title,
    toggleEnabled,
    loading,
    containerClassName: constainerClassName = 'inline-block',
  } = props;

  const hasChildren = Boolean(children);

  const IconTemplate = useSlot(props, 'Icon');
  const SuffixIconTemplate = useSlot(props, 'SuffixIcon');

  const disabledClasses = useMemo(() => (disabled || loading ? 'cursor-not-allowed' : 'hover:scale-105 focus:scale-105'), [disabled, loading]);

  // Different button type styles
  const buttonClasses = useMemo(() => {
    switch (type) {
      default:
      case ButtonType.PRIMARY:
        return `${
          disabled || loading ? 'bg-accent-1-35 text-accent-1-75' : 'bg-accent-1-100 active:bg-accent-1-75 text-white'
        } font-medium border-2 border-transparent shadow-md`;
      case ButtonType.SECONDARY:
        return `${
          disabled || loading
            ? 'border-primary-55 text-primary-55 opacity-50'
            : 'border-primary-100 active:border-primary-75 text-primary-100 active:text-primary-75'
        } bg-transparent font-medium border-2`;
      case ButtonType.TOGGLE:
        return `${disabled || loading ? 'opacity-50' : ''} ${
          toggleEnabled ? 'bg-primary-100 text-white' : 'bg-greyscale-5 text-primary-100'
        } font-medium py-2 px-4 border-0 active:border-0 shadow-md`;
      case ButtonType.TERTIARY:
        return `${
          disabled || loading
            ? '[&>div>.button-text]:after:border-current text-primary-55'
            : '[&>div>.button-text]:after:border-current [&>div>.button-text]:after:focus:border-current'
        } bg-transparent relative cursor-pointer font-medium [&>div>.button-text]:after:border-b-2 [&>div>.button-text]:after:w-full [&>div>.button-text]:after:absolute [&>div>.button-text]:after:-bottom-px`;
      case ButtonType.ICON:
        return `${
          disabled || loading
            ? '[&>div>.button-text]:after:border-current text-primary-55'
            : '[&>div>.button-text]:after:border-current [&>div>.button-text]:after:focus:border-current'
        } relative font-medium [&>div>.button-text]:after:border-b-2 [&>div>.button-text]:after:w-full [&>div>.button-text]:after:absolute [&>div>.button-text]:after:-bottom-px`;
    }
  }, [disabled, loading, toggleEnabled, type]);

  const buttonSizeClasses = useMemo(() => {
    switch (size) {
      case ButtonSize.S:
        return `text-pbk-14 py-1 px-2 ${!ignoreMinWidth ? 'min-w-20' : ''}`;
      default:
      case ButtonSize.M:
        return `text-pbk-16 py-2 px-4 ${!ignoreMinWidth ? 'min-w-32' : ''}`;
    }
  }, [ignoreMinWidth, size]);

  return (
    <div className={constainerClassName}>
      <Tooltip text={title}>
        {(tooltip) => (
          <button
            data-toggle-enabled={toggleEnabled}
            {...tooltip}
            className={`shrink-0 transform whitespace-nowrap transition-all duration-300 ease-in-out will-change-transform ${buttonClasses} ${buttonSizeClasses} ${disabledClasses} ${borderClass} ${className}`}
            name={name}
            type={htmlType}
            disabled={disabled || loading}
            onClick={onClick}
            tabIndex={0}
            {...dataAttributeProps(props)}
            {...ariaAttributeProps(props)}
          >
            {loading && (
              <div className="flex items-center justify-center">
                <Loader size={6} centered={false} />
              </div>
            )}

            {!loading && (
              <div className="flex justify-center">
                {!IconTemplate.isEmpty && (
                  <span className="pr-2">
                    <IconTemplate />
                  </span>
                )}
                {hasChildren && <div className="button-text relative flex items-center justify-center">{children}</div>}
                {!SuffixIconTemplate.isEmpty && (
                  <span className="pl-2">
                    <SuffixIconTemplate />
                  </span>
                )}
              </div>
            )}
          </button>
        )}
      </Tooltip>
    </div>
  );
});

export default Button;
